/**
 * Native App Detection Hook
 * 
 * Supports both Capacitor (Linus) and BuildNatively detection.
 * Capacitor is checked first for Linus-built apps.
 * Detection runs in main.tsx BEFORE React renders and stores result in:
 * 1. window.__NATIVE_DETECTION__ (synchronous access)
 * 2. sessionStorage (persistence)
 */

import { Capacitor } from '@capacitor/core';

export interface NativeAppDetection {
  isNativeApp: boolean;
  platform: 'ios' | 'android' | null;
  isIOSApp: boolean;
  isAndroidApp: boolean;
}

/**
 * React hook for native app detection
 * Reads from window.__NATIVE_DETECTION__ set by main.tsx before React renders
 */
export const useNativeApp = (): NativeAppDetection => {
  // First check Capacitor (Linus builds use Capacitor)
  try {
    if (Capacitor.isNativePlatform()) {
      const platform = Capacitor.getPlatform() as 'ios' | 'android' | 'web';
      if (platform === 'ios' || platform === 'android') {
        const result = {
          isNativeApp: true,
          platform,
          isIOSApp: platform === 'ios',
          isAndroidApp: platform === 'android',
        };
        console.log('[useNativeApp] Capacitor detected:', result);
        return result;
      }
    }
  } catch (e) {
    // Capacitor not available, continue to other detection methods
  }

  // Read from window object (set synchronously in main.tsx before React renders)
  const windowDetection = window.__NATIVE_DETECTION__;
  
  if (windowDetection) {
    const result = {
      isNativeApp: windowDetection.isNative,
      platform: windowDetection.platform,
      isIOSApp: windowDetection.isNative && windowDetection.platform === 'ios',
      isAndroidApp: windowDetection.isNative && windowDetection.platform === 'android',
    };
    console.log('[useNativeApp] Window detection:', result);
    return result;
  }

  // Fallback to sessionStorage (should not be needed, but just in case)
  try {
    const isNative = sessionStorage.getItem('native_container') === 'natively';
    const platform = sessionStorage.getItem('native_platform') as 'ios' | 'android' | null;
    
    const result = {
      isNativeApp: isNative,
      platform,
      isIOSApp: isNative && platform === 'ios',
      isAndroidApp: isNative && platform === 'android',
    };
    console.log('[useNativeApp] Fallback returning:', result);
    return result;
  } catch (e) {
    console.warn('[useNativeApp] sessionStorage not available');
  }
  
  // Default: not native
  return {
    isNativeApp: false,
    platform: null,
    isIOSApp: false,
    isAndroidApp: false,
  };
};

/**
 * Synchronous helper for non-React code
 */
export const isNativeEnvironment = (): boolean => {
  // Check Capacitor first
  try {
    if (Capacitor.isNativePlatform()) return true;
  } catch (e) {}
  
  const windowDetection = window.__NATIVE_DETECTION__;
  if (windowDetection) return windowDetection.isNative;
  try {
    return sessionStorage.getItem('native_container') === 'natively';
  } catch (e) {
    return false;
  }
};

/**
 * Get native platform synchronously
 */
export const getNativePlatform = (): 'ios' | 'android' | null => {
  // Check Capacitor first
  try {
    if (Capacitor.isNativePlatform()) {
      const platform = Capacitor.getPlatform();
      if (platform === 'ios' || platform === 'android') return platform;
    }
  } catch (e) {}
  
  const windowDetection = window.__NATIVE_DETECTION__;
  if (windowDetection) return windowDetection.platform;
  try {
    return sessionStorage.getItem('native_platform') as 'ios' | 'android' | null;
  } catch (e) {
    return null;
  }
};
